// angular
import {Component, OnInit, inject} from "@angular/core";
import {NgClass} from "@angular/common";
import {NavigationEnd, Router, RouterOutlet} from "@angular/router";
// material angular
import {
  MatDrawer,
  MatDrawerContainer,
  MatDrawerContent,
} from "@angular/material/sidenav";
// components
import {SidebarComponent} from "./sidebar/sidebar.component";
import {LayoutHeaderComponent} from "./layout-header/layout-header.component";
import {PageHeaderComponent} from "./page-header/page-header.component";
import {QuickAccessComponent} from "./quick-access/quick-access.component";
import {MobileNavigationComponent} from "./mobile-navigation/mobile-navigation.component";
// services
import {ScreenSizeService} from "../shared/services/screen-size.service";
import {LayoutService} from "./shared/services/layout.service";
// types
import {ScreenSizeType} from "../shared/types/screen-size.type";
// utils
import scrollTo from "../shared/utils/scrollTo";

@Component({
  selector: "app-layout",
  templateUrl: "./layout.component.html",
  styleUrls: ["./layout.component.scss"],
  standalone: true,
  imports: [
    RouterOutlet,
    NgClass,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    SidebarComponent,
    LayoutHeaderComponent,
    PageHeaderComponent,
    QuickAccessComponent,
    MobileNavigationComponent,
  ],
})
export class LayoutComponent implements OnInit {
  public dir: "ltr" | "rtl";
  public menuIsOpen?: boolean;
  public screenSize: ScreenSizeType = "unknown";
  public hiddenHeader: boolean = false;
  private _previousUrl!: string;

  // dependencies
  private _router = inject(Router);
  private _screenSizeService = inject(ScreenSizeService);
  private _layoutService = inject(LayoutService);
  public tableFullscreenIsOpen = this._layoutService.tableFullscreenIsOpen;

  constructor() {
    // set hidden header state
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (event.urlAfterRedirects.includes("/messaging")) {
          this.hiddenHeader = true;
        } else {
          this.hiddenHeader = false;
        }
      }
    });
  }

  ngOnInit() {
    this._layoutService.menuIsOpen$.subscribe(
      (isOpen: boolean) => (this.menuIsOpen = isOpen)
    );
    this._screenSizeService.screenSize$.subscribe(
      (size) => (this.screenSize = size)
    );
    // Initialize _previousUrl with current URL
    this._previousUrl = this._router.url;

    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.urlAfterRedirects;

        // Check if navigating between child routes of the same parent
        const isChildRouteNavigation = this.isSameParentRoute(
          this._previousUrl,
          currentUrl
        );

        // Only scroll to top when navigating to a different parent route
        if (!isChildRouteNavigation) {
          scrollTo({top: 0});
        }

        this._previousUrl = currentUrl;
      }
    });
  }

  private isSameParentRoute(url1: string, url2: string): boolean {
    if (!url1 || !url2) return false;

    const getParentRoute = (url: string) => {
      // Remove query params and hash
      const cleanUrl = url.split("?")[0].split("#")[0];
      const segments = cleanUrl.split("/").filter((s) => s);

      // If only one segment, there's no parent route
      if (segments.length <= 1) return "";

      // Remove last segment
      segments.pop();
      return segments.join("/");
    };

    const parent1 = getParentRoute(url1);
    const parent2 = getParentRoute(url2);

    // Both must have a parent route and they must match
    return parent1 !== "" && parent2 !== "" && parent1 === parent2;
  }

  public closeSidenav = () => {
    if (["small", "xSmall", "medium"].includes(this.screenSize)) {
      this._layoutService.menuIsOpen$.next(false);
    }
  };
}
